import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { CatalogSelectField, CharacterCount, FormDialog, TextField } from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { usePermission } from '@/shared/hooks/usePermission';
import { queryKeys } from '@/shared/query';
import { treatmentCategoryAPI } from '../api/treatmentCategory.api';

const CATEGORY_NAME_MAX_LENGTH = 100;

interface TreatmentCategoryFieldProps {
  value: string;
  onChange: (categoryId: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Keep an inactive category option visible when editing an existing item. */
  includeCategoryId?: string;
}

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof AxiosError && err.response?.data?.message
    ? String(err.response.data.message)
    : fallback;

/**
 * Select danh mục dịch vụ — quick create «Thêm danh mục» neo cuối menu.
 */
const TreatmentCategoryField: React.FC<TreatmentCategoryFieldProps> = ({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  includeCategoryId,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const canCreate = hasPermission('treatment-catalog:create') && !disabled;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const categoriesQuery = useQuery({
    queryKey: queryKeys.treatmentCatalog.categories({ includeInactive: Boolean(includeCategoryId) }),
    queryFn: async () => {
      const res = await treatmentCategoryAPI.getAll(Boolean(includeCategoryId));
      return res.data ?? [];
    },
  });

  const categories = (categoriesQuery.data ?? []).filter(
    (category) => category.isActive || category.id === includeCategoryId
  );

  const options = useMemo(
    () =>
      categories.map((category) => ({
        value: category.id,
        label: !category.isActive
          ? `${category.name} (${t('categoryCatalog.status.inactive')})`
          : category.name,
      })),
    [categories, t]
  );

  useEffect(() => {
    if (!dialogOpen) return;
    setNewName('');
  }, [dialogOpen]);

  const createMutation = useMutation({
    mutationFn: (name: string) => treatmentCategoryAPI.create({ name }),
    onSuccess: async (res) => {
      const created = res.data;
      toast.success(t('categoryCatalog.toasts.created'));
      setDialogOpen(false);
      setNewName('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.treatmentCatalog.all });
      if (created?.id) {
        onChange(created.id);
      }
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t('categoryCatalog.toasts.saveFailed')));
    },
  });

  const busy = createMutation.isPending;

  const handleCreate = (): void => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error(t('categoryCatalog.validation.nameRequired'));
      return;
    }
    createMutation.mutate(trimmed);
  };

  return (
    <>
      <CatalogSelectField
        value={value}
        onChange={onChange}
        options={options}
        label={t('fields.category')}
        placeholder={t('catalog.placeholders.category')}
        required={required}
        disabled={disabled}
        error={error}
        quickCreate={
          canCreate
            ? {
                canCreate: true,
                createLabel: t('categoryCatalog.actions.addQuick'),
                onQuickCreate: () => setDialogOpen(true),
              }
            : undefined
        }
      />

      <FormDialog
        open={dialogOpen}
        title={t('categoryCatalog.dialogs.addTitle')}
        busy={busy}
        onClose={() => setDialogOpen(false)}
        minHeight={200}
        maxWidth="xs"
        onSubmit={handleCreate}
        submitLabel={t('common:save')}
        busySubmitLabel={t('common:saving')}
      >
        <TextField
          autoFocus
          fullWidth
          required
          label={t('categoryCatalog.form.name')}
          placeholder={t('categoryCatalog.quickCreate.namePlaceholder')}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={busy}
          labelEnd={<CharacterCount current={newName.length} max={CATEGORY_NAME_MAX_LENGTH} />}
          slotProps={{ htmlInput: { maxLength: CATEGORY_NAME_MAX_LENGTH } }}
        />
      </FormDialog>
    </>
  );
};

export default TreatmentCategoryField;
